import { generateClient } from 'aws-amplify/api';
import { v4 as uuidv4 } from 'uuid';

import Log from '@/Logger';

import { getAccessToken, getAuthToken, getJWT } from './auth';
import { GraphQLResponseError } from './types';
import type { GeneratedQuery, GeneratedMutation, GeneratedSubscription, Subscription, GraphqlSubscriptionMessage } from './types';
import { handleError } from './error';

// リクエストID生成（並列実行の識別用）
export function generateRequestId(): string {
    return uuidv4();
}

/**
 * GraphQL クエリを実行する共通ヘルパー関数
 * 
 * @param query - GraphQL クエリドキュメント
 * @param variables - クエリ変数 (オプション)
 * @param requestId - リクエストID (オプション、未指定時は自動生成)
 * @returns クエリ結果
 */
export async function executeQuery<InputType = any, OutputType = any>(
    query: GeneratedQuery<InputType, OutputType>,
    variables?: InputType,
    requestId?: string
): Promise<OutputType> {
    const client = generateClient(); // リクエスト毎に新しいクライアント
    const reqId = requestId || generateRequestId(); // 外部指定またはここで生成
    const logger = Log.getLogger();
    const startTime = Date.now();

    try {
        logger.api.info('API Client', 'Query Start', `[${reqId}] Executing query`);

        const result = await client.graphql({
            query,
            variables,
            authToken: await getAuthToken()
        });

        const duration = Date.now() - startTime;
        logger.api.info('API Client', 'Query Success', `[${reqId}] Query completed in ${duration}ms`);

        // error check
        GraphQLResponseError.throwIfErrors(result, { requestId: reqId, operation: 'executeQuery' });

        // FixedQueryResult<OutputType>が取り得るPagedListは、
        // Amplify @model の自動生成API専用なので、
        // ここでは直接OutputTypeに置き換える
        return result.data as OutputType;

    } catch (e) {
        const duration = Date.now() - startTime;
        const error = handleError(e);
        logger.api.error('API Client', 'Query Error', `[${reqId}] Query failed after ${duration}ms: ${error}`);
        throw error;
    }
}

/**
 * GraphQL ミューテーションを実行する共通ヘルパー関数
 * 
 * @param mutation - GraphQL ミューテーションドキュメント
 * @param variables - ミューテーション変数 (オプション)
 * @param requestId - リクエストID (オプション、未指定時は自動生成)
 * @param headers - 追加のリクエストヘッダー (オプション)
 * @returns ミューテーション結果
 */
export async function executeMutation<InputType = any, OutputType = any>(
    mutation: GeneratedMutation<InputType, OutputType>,
    variables?: InputType,
    requestId?: string,
    headers?: Record<string, string>,
): Promise<OutputType> {
    const client = generateClient(); // リクエスト毎に新しいクライアント
    const reqId = requestId || generateRequestId(); // 外部指定またはここで生成
    const logger = Log.getLogger();
    const startTime = Date.now();

    try {
        logger.api.info('API Client', 'Mutation Start', `[${reqId}] Executing mutation`);

        const result = await client.graphql({
            query: mutation,
            variables,
            authToken: await getAuthToken()
        }, headers);

        const duration = Date.now() - startTime;
        logger.api.info('API Client', 'Mutation Success', `[${reqId}] Mutation completed in ${duration}ms`);

        // error check
        GraphQLResponseError.throwIfErrors(result, { requestId: reqId, operation: 'executeMutation' });

        // NeverEmpty<OutputType>による厳密な型変換は、
        // Amplify @model の自動生成API専用なので、
        // ここでは直接OutputTypeに置き換える
        return result.data as OutputType;

    } catch (e) {
        const duration = Date.now() - startTime;
        const error = handleError(e);
        logger.api.error('API Client', 'Mutation Error', `[${reqId}] Mutation failed after ${duration}ms: ${error}`);
        throw error;
    }
}

/**
 * GraphQL サブスクリプションを実行する共通ヘルパー関数
 * 
 * @param subscription - GraphQL サブスクリプションドキュメント
 * @param variables - サブスクリプション変数 (オプション)
 * @param delegate - Observer delegate (next, error, complete ハンドラー)
 * @param requestId - リクエストID (オプション、未指定時は自動生成)
 * @returns サブスクリプション Subscription
 */
export async function executeSubscription<InputType = any, OutputType = any>(
    subscription: GeneratedSubscription<InputType, OutputType>,
    variables?: InputType,
    delegate?: {
        next?: (value: OutputType) => void;
        error?: (error: any) => void;
        complete?: () => void;
    },
    requestId?: string
): Promise<Subscription> {
    const client = generateClient(); // 並列安全：リクエスト毎に新しいクライアント
    const reqId = requestId || generateRequestId(); // 外部指定またはここで生成
    const logger = Log.getLogger();

    try {
        logger.api.info('API Client', 'Subscription Start', `[${reqId}] Starting subscription`);

        return client.graphql({
            query: subscription,
            variables,
            authToken: await getAuthToken()
        }).subscribe({
            next: (m: GraphqlSubscriptionMessage<any>) => {
                logger.api.debug('API Client', 'Subscription Data', `[${reqId}] Received subscription data`);
                delegate?.next?.(m.data as OutputType);
            },
            error: (e) => {
                const error = handleError(e);
                logger.api.error('API Client', 'Subscription Error', `[${reqId}] Subscription error: ${error}`);
                delegate?.error?.(error);
            },
            complete: () => {
                logger.api.info('API Client', 'Subscription Complete', `[${reqId}] Subscription completed`);
                delegate?.complete?.();
            }
        });

    } catch (e) {
        const error = handleError(e);
        logger.api.error('API Client', 'Subscription Error', `[${reqId}] Subscription failed: ${error}`);
        throw error;
    }
}

// 再エクスポート: 外部から利用可能な共通ヘルパー
export { handleError, getAccessToken, getJWT };
